import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';

import {
  EventCardSkeleton,
  EventCard,
  TranslationProvider,
  CollectionHeadline,
  containerQueryProps,
} from '@resy/ui-kitchen';

import './EventList.scss';

const propTypes = {
  className: PropTypes.string,
  containerQuery: containerQueryProps,
  description: PropTypes.string,
  disableHeadline: PropTypes.bool,
  events: PropTypes.arrayOf(PropTypes.object),
  homepageModule: PropTypes.bool,
  isGda: PropTypes.bool,
  loading: PropTypes.bool,
  locationSlug: PropTypes.string.isRequired,
  maxCards: PropTypes.number,
  name: PropTypes.string,
  showSeeMore: PropTypes.bool,
  totalEvents: PropTypes.number,
};

const defaultProps = {
  className: undefined,
  containerQuery: {},
  description: undefined,
  disableHeadline: false,
  events: [],
  homepageModule: false,
  isGda: false,
  loading: false,
  maxCards: 10,
  name: undefined,
  showSeeMore: true,
  totalEvents: 0,
};

const EventList = ({
  loading,
  name,
  description,
  locationSlug,
  events,
  totalEvents,
  isGda,
  homepageModule,
  maxCards,
  showSeeMore,
  disableHeadline,
  className,
  containerQuery,
}) => {
  const renderHeadline = () => {
    if (homepageModule) {
      if (totalEvents === 0 || totalEvents === null) {
        return null;
      }

      return (
        <div className="EventList__inner-container container">
          {(totalEvents > maxCards && showSeeMore) ? (
            <CollectionHeadline
              badge="https://s3.amazonaws.com/resy.com/images/lists/events.svg"
              title={name || 'Special Events &amp; Experiences'}
              translationKey={name ? null : 'special_events'}
              description={description}
              link={`/cities/${locationSlug}/events`}
              isEvents
            />
          ) : (
            <CollectionHeadline
              badge="https://s3.amazonaws.com/resy.com/images/lists/events.svg"
              title={name || 'Special Events &amp; Experiences'}
              description={description}
              translationKey={name ? null : 'special_events'}
            />
          )}
        </div>
      );
    }

    return null;
  };

  const renderNull = () => {
    if (loading) {
      return (
        <div className="EventList__inner-container container">
          <div className="EventList__events-container">
            <EventCardSkeleton />
            <EventCardSkeleton />
            <EventCardSkeleton />
            <EventCardSkeleton />
            <EventCardSkeleton />
          </div>
        </div>
      );
    }

    if (homepageModule) {
      return null;
    }

    return (
      <div className="EventList__inner-container container">
        <div className="EventList__null-container">
          <TranslationProvider
            translationKey="no_events"
          >
            <div className="EventList__null-title">
              There are no upcoming special events and experiences.
            </div>
          </TranslationProvider>
          <TranslationProvider
            translationKey="check_later"
          >
            <div className="EventList__null-body">
              Check back later, or try another location.
            </div>
          </TranslationProvider>
        </div>
      </div>
    );
  };

  const renderEvents = () => {
    let eventsToRender = events;

    if (isGda) events[0].is_pinned = false;

    if (homepageModule) {
      // eslint-disable-next-line camelcase
      if (events[0]?.is_pinned) {
        eventsToRender = events.slice(0, 9);
      } else {
        eventsToRender = events.slice(0, maxCards);
      }
    }

    return (
      <div className="EventList__inner-container container">
        <div className={cx('EventList__events-container', {
          [className]: className,
          'EventList__events-container--truncated': homepageModule && eventsToRender.length > 6,
          'EventList__events-container--fixed-width': homepageModule && eventsToRender.length < 5,
        })}
        >
          {eventsToRender.map((event) => (
            <EventCard
              {...event}
              key={event.url_slug}
              isGda={isGda}
              venue={{
                ...event.venue,
                urlSlug: event.venue.url_slug,
              }}
              containerQuery={containerQuery}
            />
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className={cx('EventList', { EventList__RGA: isGda })}>
      {!disableHeadline && renderHeadline()}
      {events.length > 0
        ? renderEvents()
        : renderNull()}
    </div>
  );
};

EventList.propTypes = propTypes;
EventList.defaultProps = defaultProps;

EventList.displayName = 'EventList';
export default EventList;
